import React, { useMemo } from 'react'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_ASSET_TRANSACTIONS } from '../../../state_manager/globals.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'
import { AssetTransactionRecordFields } from '../../../state_manager/data_models/asset_transaction_record.ts'
import type { FilterOptions, FilterConfig } from '../../../core/components/TableView/TableViewTypes.ts'

interface AssetTransactionsTableProps {
  data: Record<string, unknown>[]
  loading: boolean
  loadingMore?: boolean
  hasMore?: boolean
  currentPage?: number
  gridColumns: ViewConfigColumn[]
  activeFilters?: FilterValue[]
  activeSorting?: SortConfig[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  onLoadMore?: (page: number, pageSize: number) => void | Promise<void>
  onFiltersChange?: (filters: FilterConfig) => void
  onSortChange?: (sortConfig: any) => void
}

export const AssetTransactionsTable = React.memo(function AssetTransactionsTable({
  data,
  loading,
  loadingMore = false,
  hasMore = false,
  currentPage = 1,
  gridColumns,
  activeFilters = [],
  activeSorting = [],
  filterOptions = {},
  filterOptionsLoading = false,
  onLoadMore,
  onFiltersChange,
  onSortChange
}: AssetTransactionsTableProps) {
  const { openFormInTab } = useTabManagerContext()

  const openRecordFullColumns = useMemo(
    () => new Set(gridColumns.filter((c) => c.fieldKey === 'open-record-full').map((c) => c.valueKey)),
    [gridColumns]
  )

  // Related-record links: a column whose cfg carries link_id_field pointing
  // at ANOTHER table's id (e.g. accounts__id) opens that record's full form
  // instead of this grid's own record. valueKey -> {idField, table}.
  // Aliases cover row keys that disambiguate two links to the SAME table
  // (both tranche columns open the tranches form).
  const relatedLinkByValueKey = useMemo(() => {
    const aliases: Record<string, string> = { tranches_created: 'tranches' }
    const m = new Map<string, { idField: string; table: string }>()
    for (const c of gridColumns) {
      if (c.fieldKey !== 'open-record-full' || !c.linkIdField) continue
      const match = /^([a-z0-9_]+)__id$/.exec(c.linkIdField)
      if (!match) continue
      const table = aliases[match[1]] ?? match[1].replace(/_/g, '-')
      if (table === 'asset-transactions') continue
      m.set(c.valueKey, { idField: c.linkIdField, table })
    }
    return m
  }, [gridColumns])

  const { columns: baseColumns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      fallbackRecordModuleKey: 'asset_transactions'
    })
  }, [gridColumns])

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      if (!opensRecordFull) return col
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)

          const related = relatedLinkByValueKey.get(String(col.key))
          if (related) {
            const relatedId = record[related.idField]
            if (typeof relatedId !== 'string' || !relatedId) return node
            return (
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => openFormInTab(related.table, relatedId, typeof value === 'string' ? value : undefined)}
              >
                {node}
              </button>
            )
          }

          const txId =
            typeof record[AssetTransactionRecordFields.asset_transactions__id] === 'string'
              ? (record[AssetTransactionRecordFields.asset_transactions__id] as string)
              : ''
          if (!txId) return node

          const title = [record.accounts__name, record.cfg_asset_transaction_types__label]
            .filter(Boolean)
            .join(' — ')

          // Unnamed rows (e.g. drafts saved without a name) render an empty
          // cell, collapsing the link to zero width. Fall back to the same
          // company — type label used for the tab title so the row stays
          // openable; muted italics mark it as a placeholder, not a name.
          const isEmptyNode = node == null || node === ''
          const statusLabel =
            typeof record.cfg_transaction_statuses__label === 'string' ? record.cfg_transaction_statuses__label : null
          const fallbackLabel = [statusLabel ? `(${statusLabel})` : null, title || 'Asset Transaction']
            .filter(Boolean)
            .join(' ')

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab(TABLE_ASSET_TRANSACTIONS, txId, title || 'Asset Transaction')}
            >
              {isEmptyNode ? <span className='italic opacity-80'>{fallbackLabel}</span> : node}
            </button>
          )
        }
      }
    })
  }, [baseColumns, openRecordFullColumns, relatedLinkByValueKey, openFormInTab])

  const dataSource = useMemo(() => {
    const src = data ?? []
    return src.map((row, i) => {
      const idValue = row[AssetTransactionRecordFields.asset_transactions__id]
      const id = typeof idValue === 'string' && idValue ? idValue : `row-${i}`
      return { ...row, id, key: id }
    })
  }, [data])

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        onLoadMore={onLoadMore}
        currentFilters={currentFilters}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={currentSortConfig}
        frozenColumns={frozenColumns}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})

export default AssetTransactionsTable
